import React, { useEffect, useMemo, useState } from 'react';
import { Header } from '../components/dashboard/Header';
import { TimeframeSelector, TimeframeOption } from '../components/dashboard/TimeframeSelector';
import { KpiCards, KpiFilter } from '../components/dashboard/KpiCards';
import { FilterBar, FilterState } from '../components/dashboard/FilterBar';
import { LeadTable } from '../components/dashboard/LeadTable';
import { EmptyState } from '../components/dashboard/EmptyStates';
import { KpiCardsSkeleton, LeadTableSkeleton } from '../components/dashboard/SkeletonStates';
import { Lead, mockLeads, isInTimeframe, isUnlicensedState, getEffectiveAnomalies } from '../data/mockLeads';
export function LeadOpsDashboard() {
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [timeframe, setTimeframe] = useState<TimeframeOption>('This Week');
  const [kpiFilter, setKpiFilter] = useState<KpiFilter>('all');
  const [leads, setLeads] = useState<Lead[]>(mockLeads);
  const [filters, setFilters] = useState<FilterState>({
    loanPurpose: [],
    leadStatus: [],
    owner: [],
    leadSource: [],
    onlyAnomalies: false
  });
  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      setLastRefreshed(new Date());
      setIsRefreshing(false);
    }, 1000);
  };
  const handleClearFilters = () => {
    setFilters({
      loanPurpose: [],
      leadStatus: [],
      owner: [],
      leadSource: [],
      onlyAnomalies: false
    });
    setKpiFilter('all');
  };
  const handleEnrollCampaign = (leadId: string, campaignName: string) => {
    setLeads(prev => prev.map(l => {
      if (l.id !== leadId) return l;
      const now = new Date().toISOString();
      return {
        ...l,
        campaignEnrollment: {
          enrolled: true,
          campaignName,
          status: 'Active',
          enrolledDate: now
        },
        // If the lead was previously flagged as missing a campaign, clear that flag.
        anomalies: (() => {
          const cleared = l.anomalies.filter(a => a !== 'Missing Campaign');
          const remaining = cleared.filter(a => a !== 'No Issues');
          return remaining.length === 0 ? ['No Issues'] : remaining;
        })()
      };
    }));
  };
  const handleChangeOwner = (leadId: string, newOwner: string) => {
    setLeads(prev => prev.map(l => {
      if (l.id !== leadId) return l;
      // Clear queue-stuck anomaly when assigning to a real owner.
      const isQueue = newOwner === 'Round Robin Queue';
      const cleared = isQueue ? l.anomalies : l.anomalies.filter(a => a !== 'Round Robin Queue');
      const remaining = cleared.filter(a => a !== 'No Issues');
      return {
        ...l,
        owner: newOwner,
        anomalies: remaining.length === 0 ? ['No Issues'] : remaining
      };
    }));
  };
  // Shared base filter: timeframe + filter bar (no kpiFilter, no onlyAnomalies)
  const baseLeads = useMemo(() => {
    return leads.filter(lead => {
      if (!isInTimeframe(lead, timeframe)) return false;
      if (filters.loanPurpose.length > 0 && !filters.loanPurpose.includes(lead.loanPurpose)) return false;
      if (filters.leadStatus.length > 0 && !filters.leadStatus.includes(lead.status)) return false;
      if (filters.owner.length > 0 && !filters.owner.includes(lead.owner)) return false;
      if (filters.leadSource.length > 0 && !filters.leadSource.includes(lead.source)) return false;
      return true;
    });
  }, [leads, filters, timeframe]);
  const filteredLeads = useMemo(() => {
    return baseLeads.filter(lead => {
      if (filters.onlyAnomalies) {
        const hasAnomalies = getEffectiveAnomalies(lead).some(a => a !== 'No Issues');
        if (!hasAnomalies) return false;
      }
      if (kpiFilter === 'queueStuck' && !getEffectiveAnomalies(lead).includes('Round Robin Queue')) return false;
      if (kpiFilter === 'missingCampaign' && !getEffectiveAnomalies(lead).includes('Missing Campaign')) return false;
      if (kpiFilter === 'enrolled' && !(lead.campaignEnrollment.enrolled || lead.scheduledDrip.scheduled)) return false;
      if (kpiFilter === 'unlicensed' && !isUnlicensedState(lead.propertyState)) return false;
      return true;
    });
  }, [baseLeads, filters.onlyAnomalies, kpiFilter]);
  // KPI counts use baseLeads (so tiles stay stable across drilldowns), but
  // they DO respect onlyAnomalies so toggling it updates the counts too.
  const kpiData = useMemo(() => {
    const base = filters.onlyAnomalies ? baseLeads.filter(l => getEffectiveAnomalies(l).some(a => a !== 'No Issues')) : baseLeads;
    return {
      totalLeads: base.length,
      queueStuck: base.filter(l => getEffectiveAnomalies(l).includes('Round Robin Queue')).length,
      missingCampaign: base.filter(l => getEffectiveAnomalies(l).includes('Missing Campaign')).length,
      enrolled: base.filter(l => l.campaignEnrollment.enrolled || l.scheduledDrip.scheduled).length,
      unlicensed: base.filter(l => isUnlicensedState(l.propertyState)).length
    };
  }, [baseLeads, filters.onlyAnomalies]);
  return <div className="min-h-screen bg-slate-50 pb-12" data-id="element-341">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-342">
        <Header lastRefreshed={lastRefreshed} onRefresh={handleRefresh} isRefreshing={isRefreshing} data-id="element-343" />

        <main data-id="element-344">
          <TimeframeSelector selected={timeframe} onChange={setTimeframe} data-id="element-345" />

          {isRefreshing ? <>
              <KpiCardsSkeleton data-id="element-346" />
              <div className="mb-6 h-[72px] bg-slate-200 rounded-2xl animate-pulse" data-id="element-347" />
              <LeadTableSkeleton data-id="element-348" />
            </> : <>
              <KpiCards data={kpiData} activeFilter={kpiFilter} onSelect={setKpiFilter} data-id="element-349" />

              <FilterBar filters={filters} onFilterChange={setFilters} onClearFilters={handleClearFilters} data-id="element-350" />

              {filteredLeads.length > 0 ? <LeadTable leads={filteredLeads} onEnrollCampaign={handleEnrollCampaign} onChangeOwner={handleChangeOwner} data-id="element-351" /> : <EmptyState type={(filters.onlyAnomalies || kpiFilter !== 'all') && leads.length > 0 ? 'no-anomalies' : 'no-leads'} onClearFilters={handleClearFilters} data-id="element-352" />}
            </>}
        </main>
      </div>
    </div>;
}